import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { CheckInCheckOutLog } from "@/interfaces/IRoster";
import { Visit, VisitStatus } from "@/interfaces/IServiceUser";
import { getId, getRRule } from "@/utils/helpers";

dayjs.extend(utc);

/**
 * How far a check-in may sit from an occurrence's scheduled start and still be
 * treated as belonging to it.
 *
 * This mirrors REPORTS_LOG_MATCH_WINDOW_MINUTES on the API, which the
 * reporting engine uses when it decides whether an occurrence was attended. If
 * that default changes, change this with it, or the roster will start
 * disagreeing with the quarterly report again.
 */
export const VISIT_LOG_MATCH_WINDOW_MINUTES = 24 * 60;

type LogRef = string | { _id?: string } | null | undefined;

type AnyLog = Pick<CheckInCheckOutLog, "checkIn" | "checkOut"> & {
  visitId?: LogRef;
  carer?: LogRef;
};

/**
 * The logs belonging to ONE occurrence, earliest check-in first.
 *
 * A recurring visit is one document with one _id, and the roster expands it
 * into occurrences that all share that _id. Matching a log by visit id alone
 * attaches one carer's check-in to every occurrence of the series, which is
 * what made a visit checked into in May show as Completed on every later day.
 * A log belongs to an occurrence only when its check-in is near that
 * occurrence's scheduled start, which is the rule the API's reporting engine
 * already applies.
 */
export const logsForOccurrence = (
  logs: AnyLog[] | undefined,
  {
    visit,
    visitId,
    occurrenceStart,
    carerId,
    windowMinutes = VISIT_LOG_MATCH_WINDOW_MINUTES,
  }: {
    visit?: Visit;
    visitId?: string;
    occurrenceStart?: Date | string | null;
    carerId?: string;
    windowMinutes?: number;
  }
): AnyLog[] => {
  if (!occurrenceStart) return [];

  const start = dayjs.utc(occurrenceStart);
  if (!start.isValid()) return [];

  let rule: ReturnType<typeof getRRule> | null = null;
  if (visit?.recurring) {
    try {
      rule = getRRule(visit);
    } catch {
      return [];
    }
  }

  return (logs || [])
    .filter((log) => Boolean(log?.checkIn))
    .filter((log) => (visitId ? getId(log?.visitId) === visitId : true))
    .filter((log) => (carerId ? getId(log?.carer) === carerId : true))
    .filter(
      (log) =>
        Math.abs(dayjs.utc(log.checkIn).diff(start, "minute")) <= windowMinutes
    )
    .filter((log) => {
      if (!rule) return true;
      const instant = dayjs.utc(log.checkIn).toDate();
      const before = rule.before(instant, true);
      const after = rule.after(instant, true);
      const left = before ? Math.abs(+instant - +before) : Infinity;
      const right = after ? Math.abs(+after - +instant) : Infinity;
      if (before && after && +before !== +after && left === right) return false;
      const nearest = left <= right ? before : after;
      return nearest?.getTime() === start.valueOf();
    })
    .sort(
      (a, b) => dayjs.utc(a.checkIn).valueOf() - dayjs.utc(b.checkIn).valueOf()
    );
};

/** The one log to show against an occurrence, or null when nobody attended. */
export const logForOccurrence = (
  logs: AnyLog[] | undefined,
  options: Parameters<typeof logsForOccurrence>[1]
): AnyLog | null => logsForOccurrence(logs, options)[0] || null;

/** A check-in with no check-out that the 72 hour sweep has not closed yet. */
const hasStalePendingCheckout = (logs: AnyLog[]) =>
  !logs.some((log) => log.checkIn && log.checkOut) &&
  logs.some(
    (log) =>
      log.checkIn &&
      !log.checkOut &&
      dayjs().diff(dayjs(log.checkIn), "hour") >= 72
  );

/**
 * The status of ONE occurrence, from the logs that actually belong to it.
 *
 * `visit.status` from the API describes the stored visit, so on a recurring
 * visit every occurrence inherits the same value. The roster expands
 * occurrences itself, so it has to work the status out per occurrence.
 */
export const getOccurrenceStatus = (
  visit: Visit | undefined,
  occurrenceStart: Date | string | null | undefined,
  carerId?: string
): VisitStatus => {
  if (!visit) return VisitStatus.UN_ALLOCATED;
  if (visit.isCancelled) return VisitStatus.CANCELLED;
  if (!visit.carerAssigned?.length) return VisitStatus.UN_ALLOCATED;

  const occurrenceLogs = logsForOccurrence(visit.logs as AnyLog[] | undefined, {
    visit,
    visitId: visit._id,
    occurrenceStart,
    carerId,
  });

  if (occurrenceLogs.some((log) => log.checkIn && log.checkOut)) {
    return VisitStatus.COMPLETED;
  }

  if (occurrenceLogs.length) return VisitStatus.IN_PROGRESS;

  // Real UTC, not dayjs().utc(true). The reporting engine compares against the
  // actual instant, so reinterpreting the browser's wall clock as UTC here is
  // what would make a visit look overdue hours early outside the UK.
  const start = occurrenceStart ? dayjs.utc(occurrenceStart) : null;
  if (start?.isValid() && dayjs.utc().isAfter(start)) {
    return VisitStatus.OVERDUE;
  }

  return VisitStatus.SCHEDULED;
};

/**
 * The occurrence status of a FullCalendar event.
 *
 * The timeline's guards used to read `extendedProps.status`, which the API
 * works out for the stored visit. On a recurring visit that is the series'
 * first occurrence, so it answered "was this series ever checked into" rather
 * than "was this occurrence completed". The guards that stop a completed visit
 * being moved, resized or reassigned need the second question.
 */
export const getEventOccurrenceStatus = (event: {
  start?: Date | string | null;
  extendedProps?: unknown;
  getResources?: () => { id: string }[];
}): VisitStatus => {
  const visit = event?.extendedProps as Visit | undefined;
  const resourceId = event?.getResources?.()?.[0]?.id;
  const carerId = visit?.carerAssigned?.some(
    (carer) => getId(carer) === resourceId
  )
    ? resourceId
    : undefined;

  return getOccurrenceStatus(visit, event?.start, carerId);
};

const STATUS_COLOR: Record<VisitStatus, string> = {
  [VisitStatus.CANCELLED]: "#000000",
  [VisitStatus.IN_PROGRESS]: "#FB8335",
  [VisitStatus.COMPLETED]: "#4AA33B",
  [VisitStatus.OVERDUE]: "#B1302E",
  [VisitStatus.SCHEDULED]: "#8824B6",
  [VisitStatus.UN_ALLOCATED]: "#9F9F9E",
};

/** A check-in left open past the 72 hour sweep reads differently from a normal one. */
const STALE_CHECKOUT_COLOR = "#4682d5";

/**
 * The colour for one occupied row of one occurrence.
 *
 * Same palette as getVisitColor, which stays in place for the screens that
 * genuinely deal with a whole visit rather than a single occurrence.
 */
export const getOccurrenceVisitColor = (
  visit: Visit | undefined,
  occurrenceStart: Date | string | null | undefined,
  carerId?: string
): { status: VisitStatus; color: string } => {
  const status = getOccurrenceStatus(visit, occurrenceStart, carerId);

  if (status !== VisitStatus.CANCELLED && visit) {
    const occurrenceLogs = logsForOccurrence(
      visit.logs as AnyLog[] | undefined,
      { visit, visitId: visit._id, occurrenceStart, carerId }
    );
    if (hasStalePendingCheckout(occurrenceLogs)) {
      return { status: VisitStatus.IN_PROGRESS, color: STALE_CHECKOUT_COLOR };
    }
  }

  return { status, color: STATUS_COLOR[status] };
};
